import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import FolderFilledIcon from '@/assets/images/icons/folder-filled.svg'
import FolderIcon from '@/assets/images/icons/folder.svg'
import PlusIcon from '@/assets/images/icons/plus-icon.svg'
import { SkeletonRow } from '@/components/ui/Skeleton'
import { useAuth } from '@/lib/AuthContext'
import { logger } from '@/lib/logger'
import {
  addCourseToMultipleFolders,
  addLessonToMultipleFolders,
  createUserFolder,
  fetchAllFolderBookmarks,
  fetchUserFolders,
  removeCourseFromFolder,
  removeLessonFromFolder,
  UserBookmark,
  UserFolder
} from '@/supabase/services'
import React, { useEffect, useState } from 'react'
import { ScrollView as RNScrollView, TouchableOpacity } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import {
  Button,
  Input,
  XStack,
  YStack
} from 'tamagui'
import { BodyText, CardTitle } from '@/components/ui/Typography'

type BookmarkContentPropsBase = {
  onSave: (isBookmarked: boolean) => void
  onClose: () => void
  showError: (msg: string) => void
  showSuccess: (msg: string) => void
  showHeader?: boolean
  isOpen?: boolean
  ScrollViewComponent?: typeof RNScrollView
}

export type BookmarkContentProps =
  | (BookmarkContentPropsBase & {
      mode: 'lesson'
      lessonId: number
      lessonUrl: string
      courseId?: number
      collectionId?: number
    })
  | (BookmarkContentPropsBase & {
      mode: 'course'
      courseId: number
      courseUrl: string
    })

export function BookmarkContent({
  onSave,
  onClose,
  showError,
  showSuccess,
  showHeader = true,
  isOpen = true,
  ScrollViewComponent = RNScrollView,
  ...modeProps
}: BookmarkContentProps) {
  const { mode } = modeProps
  const itemLabel = mode === 'course' ? 'Course' : 'Lesson'
  const insets = useSafeAreaInsets()
  const footerBottomPad = Math.max(insets.bottom, 20)

  const { user } = useAuth()

  const [pendingAdd, setPendingAdd] = useState<Set<number>>(new Set())
  const [pendingRemove, setPendingRemove] = useState<Set<number>>(new Set())
  const [folders, setFolders] = useState<UserFolder[]>([])
  const [folderBookmarks, setFolderBookmarks] = useState<Record<number, UserBookmark[]>>({})
  const [newFolderName, setNewFolderName] = useState('')
  const [isNewFolderMode, setIsNewFolderMode] = useState(false)
  const [foldersLoading, setFoldersLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [loadError, setLoadError] = useState<Error | null>(null)

  const matchesBookmark = (b: UserBookmark): boolean =>
    mode === 'course'
      ? b.course_id === modeProps.courseId && b.lesson_id == null
      : b.lesson_id === modeProps.lessonId

  const isBookmarkedIn = (grouped: Record<number, UserBookmark[]>): boolean =>
    Object.values(grouped).some((list) => list?.some(matchesBookmark))

  const loadUserFolders = React.useCallback(async () => {
    if (!user?.id) return
    try {
      setFoldersLoading(true)
      setLoadError(null)
      const [userFolders, grouped] = await Promise.all([
        fetchUserFolders(user.id),
        fetchAllFolderBookmarks(user.id),
      ])
      setFolders(userFolders)
      setFolderBookmarks(grouped)
      setPendingAdd(new Set())
      setPendingRemove(new Set())
    } catch (error: any) {
      logger.error('Error loading user folders:', error)
      setLoadError(error)
    } finally {
      setFoldersLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (isOpen) loadUserFolders()
  }, [isOpen, loadUserFolders])

  // --- Save logic ---

  async function applyFolderChanges(userId: string, addIds: number[], removeIds: number[]) {
    let added = 0
    const addErrors: string[] = []

    if (addIds.length > 0) {
      const result =
        mode === 'course'
          ? await addCourseToMultipleFolders(addIds, modeProps.courseId, userId, modeProps.courseUrl)
          : await addLessonToMultipleFolders(addIds, modeProps.lessonId, userId, modeProps.lessonUrl, modeProps.courseId)
      added = result.success
      addErrors.push(...result.errors)
    }

    let removalErrors = 0
    for (const fid of removeIds) {
      try {
        const ok =
          mode === 'course'
            ? await removeCourseFromFolder(fid, modeProps.courseId, userId)
            : await removeLessonFromFolder(fid, modeProps.lessonId, userId)
        if (!ok) removalErrors += 1
      } catch (error) {
        logger.error(`Failed to remove ${itemLabel.toLowerCase()} bookmark:`, { folderId: fid, userId, error })
        removalErrors += 1
      }
    }

    return { added, removed: removeIds.length - removalErrors, addErrors, removalErrors }
  }

  const handleSave = async () => {
    if (!user?.id) return
    const addIds = Array.from(pendingAdd)
    const removeIds = Array.from(pendingRemove)
    if (addIds.length === 0 && removeIds.length === 0) {
      showError('Please select at least one change')
      return
    }

    setActionLoading(true)
    try {
      const { added, removed, addErrors, removalErrors } = await applyFolderChanges(user.id, addIds, removeIds)

      if (addErrors.length > 0 || removalErrors > 0) {
        const parts: string[] = []
        if (addErrors.length > 0) {
          const isNetwork = addErrors[0]?.includes('No internet') || addErrors[0]?.includes('network') || addErrors[0]?.includes('Failed to fetch')
          parts.push(isNetwork ? 'Failed to add to folder(s)' : addErrors[0] ?? 'Failed to add')
        }
        if (removalErrors > 0) parts.push(`Failed to remove from ${removalErrors} folder${removalErrors > 1 ? 's' : ''}`)
        showError(parts.join(' and ') + '. Please try again.')
      }

      if (added > 0 || removed > 0) {
        const addMsg = added > 0 ? `added to ${added} folder${added > 1 ? 's' : ''}` : ''
        const remMsg = removed > 0 ? `removed from ${removed} folder${removed > 1 ? 's' : ''}` : ''
        const sep = addMsg && remMsg ? ', and ' : ''
        showSuccess(`${itemLabel} ${addMsg}${sep}${remMsg}`)
        try {
          const grouped = await fetchAllFolderBookmarks(user.id)
          setFolderBookmarks(grouped)
          onSave(isBookmarkedIn(grouped))
          setPendingAdd(new Set())
          setPendingRemove(new Set())
        } catch (error) {
          logger.error('Failed to refresh folder bookmarks after save:', error)
        }
        setTimeout(onClose, 1000)
      } else if (addErrors.length === 0 && removalErrors === 0) {
        showError('No changes were applied. Please try again.')
      }
    } catch (error) {
      logger.error(`Error saving ${itemLabel.toLowerCase()} bookmark:`, error)
      showError(`Failed to save ${itemLabel.toLowerCase()}. Please try again.`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleNewFolder = async () => {
    if (!newFolderName.trim() || !user?.id) {
      showError('Please enter a folder name')
      return
    }

    setActionLoading(true)
    
    try {
      const newFolder = await createUserFolder(newFolderName.trim(), user.id)
      showSuccess('Folder created successfully!')
      setFolders(prev => [newFolder, ...prev])
      setNewFolderName('')
      setIsNewFolderMode(false)
    } catch (error) {
      logger.error('Failed to create folder:', {
        userId: user.id,
        folderName: newFolderName.trim(),
        error
      })
      showError('Failed to create folder. Please try again.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleBackToFolders = () => {
    setIsNewFolderMode(false)
    setNewFolderName('')
  }

  const isItemInFolder = (folderId: number): boolean =>
    (folderBookmarks[folderId] || []).some(matchesBookmark)

  const toggleFolderSelection = (folderId: number) => {
    const hadItem = isItemInFolder(folderId)
    if (hadItem) {
      setPendingRemove(prev => {
        const next = new Set(prev)
        if (next.has(folderId)) next.delete(folderId)
        else next.add(folderId)
        return next
      })
    } else {
      setPendingAdd((prev) => {
        const next = new Set(prev)
        if (next.has(folderId)) next.delete(folderId)
        else next.add(folderId)
        return next
      })
    }
  }

  return (
    <YStack
      flex={1}
      minHeight={0}
      width="100%"
      backgroundColor="#FFFFFF"
      borderRadius={16}
      overflow="hidden"
    >
      {showHeader && (
        <YStack
          flexShrink={0}
          p="$3"
          backgroundColor="white"
          borderTopLeftRadius="$4"
          borderTopRightRadius="$4"
        >
          <XStack jc="flex-start" ai="center" w="100%">
            {isNewFolderMode ? (
              <XStack ai="center" gap="$2">
                <TouchableOpacity onPress={handleBackToFolders}>
                  <ArrowLeftIcon width={20} height={20} />
                </TouchableOpacity>
                <CardTitle fontWeight="600" color="#0D0D0D">
                  New Folder
                </CardTitle>
              </XStack>
            ) : (
              <CardTitle fontWeight="600" color="#0D0D0D">
                Save to...
              </CardTitle>
            )}
          </XStack>
        </YStack>
      )}

      {!loadError && (
        <YStack flexShrink={0} px="$3" pt="$2" pb="$2" gap="$3" backgroundColor="white">
          {!isNewFolderMode ? (
            <TouchableOpacity onPress={() => setIsNewFolderMode(true)}>
              <XStack 
                ai="center" 
                jc="center"
                gap="$3" 
                borderWidth={1}
                borderColor="#D6D3CE"
                borderRadius={14}
                backgroundColor="#FFFFFF"
                paddingVertical={3}
                paddingHorizontal={16}
              >
                <PlusIcon width={32} height={32} />
                <CardTitle fontWeight="600" color="#0D0D0D">
                  New Folder
                </CardTitle>
              </XStack>
            </TouchableOpacity>
          ) : (
            <Input
              placeholder="Enter folder name..."
              value={newFolderName}
              onChangeText={setNewFolderName}
              onSubmitEditing={handleNewFolder}
              borderWidth={1}
              borderColor="#D6D3CE"
              borderRadius={14}
              backgroundColor="#FFFFFF"
              paddingHorizontal={16}
              fontSize={16}
              color="#0D0D0D"
              placeholderTextColor="#999999"
              returnKeyType="done"
              editable={true}
              multiline={false}
              minHeight={40}
              height={40}
            />
          )}
        </YStack>
      )}

      <ScrollViewComponent
        style={{ flex: 1, minHeight: 0, backgroundColor: '#FFFFFF' }}
        contentContainerStyle={{ flexGrow: 1, backgroundColor: '#FFFFFF' }}
        showsVerticalScrollIndicator={false}
        bounces={false}
        contentInsetAdjustmentBehavior="never"
      >
        <YStack flexGrow={1} backgroundColor="#FFFFFF" p="$3" gap="$3">
          {/* Folder List with Accordion - Only show when not in new folder mode */}
          {!isNewFolderMode && (
            <YStack gap="$1">
            {foldersLoading ? (
              <YStack gap="$2">
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </YStack>
            ) : loadError ? (
              <YStack ai="center" p="$4" gap="$3">
                <BodyText color="#666" textAlign="center">
                  {loadError?.message || 'Failed to load folders'}
                </BodyText>
                <Button
                  size="$3"
                  bg="#374151"
                  color="white"
                  br="$6"
                  onPress={loadUserFolders}
                  pressStyle={{ opacity: 0.8 }}
                >
                  Try again
                </Button>
              </YStack>
            ) : folders.length === 0 ? (
              <YStack ai="center" p="$4">
                <BodyText color="#666">No folders yet. Create one!</BodyText>
              </YStack>
            ) : (
              folders.map((folder) => {
                const hasItem = isItemInFolder(folder.id)
                const willHaveItem = hasItem ? !pendingRemove.has(folder.id) : pendingAdd.has(folder.id)
                const isChanged = pendingAdd.has(folder.id) || pendingRemove.has(folder.id)

                return (
                  <TouchableOpacity
                    key={folder.id}
                    onPress={() => toggleFolderSelection(folder.id)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: isChanged ? '#F4F1ED' : 'transparent',
                      borderWidth: 0,
                      borderColor: 'transparent'
                    }}
                  >
                    <YStack mr={12}>
                      {willHaveItem ? (
                        <FolderFilledIcon width={20} height={20} color="#000000" />
                      ) : (
                        <FolderIcon width={20} height={20} color="#666666" />
                      )}
                    </YStack>
                    
                    <CardTitle 
                      fontWeight="600" 
                      color="#0D0D0D" 
                      flex={1}
                    >
                      {folder.title}
                    </CardTitle>
                  </TouchableOpacity>
                )
              })
            )}
          </YStack>
          )}
        </YStack>
      </ScrollViewComponent>

      {!loadError && (
        <YStack
          flexShrink={0}
          pt="$3"
          px="$3"
          pb={footerBottomPad}
          backgroundColor="white"
          borderBottomLeftRadius="$4"
          borderBottomRightRadius="$4"
        >
          <Button
            onPress={isNewFolderMode ? handleNewFolder : handleSave}
            backgroundColor="#0D0D0D"
            color="white"
            fontWeight="600"
            size="$3"
            borderRadius={30}
            disabled={isNewFolderMode ? !newFolderName.trim() || actionLoading : (pendingAdd.size === 0 && pendingRemove.size === 0) || actionLoading}
            opacity={isNewFolderMode ? (newFolderName.trim() && !actionLoading ? 1 : 0.5) : ((pendingAdd.size > 0 || pendingRemove.size > 0) && !actionLoading ? 1 : 0.5)}
          >
            {actionLoading ? 'Saving...' : (isNewFolderMode ? 'Save' : 'Done')}
          </Button>
        </YStack>
      )}
    </YStack>
  )
}
